import Image from "next/image";
import Link from "next/link";
import { cn } from "@/libs/utils";
import { useTrans } from "@/utils/useTrans";
import { useStaggerAnimation } from "@/hooks/useStaggerAnimation";
import { Button } from "@/v2-components/ui/button";
import SectionHeading from "@/components/home/SectionHeading";

/**
 * "나에게 맞는 TactSuit" — 피그마 S4 ProductHero(28983:88589).
 *
 * ⚠️ 버튼은 **하나**다 — 통합 PDP(`/shop/buy-tactsuit/...`)로 가는 primary 뿐이다(#5289).
 *   종전 목적지 `/tactsuit/{slug}` 는 **307 리다이렉트**라 왕복이 한 번 더 들었다
 *   (#5068 로 소개 페이지 제거).
 *   종전엔 `Buy Now`(옵션 선택 페이지) + 상세 두 개였는데, 그건 **PDP 미출시 동안의 임시
 *   상태**였다. #5303 이 그렇게 적고 있다 — "PDP 나가면 자세히보기만 남기는 것으로".
 *   PDP 가 이 에픽으로 나가므로 구매 버튼을 걷어냈다.
 *
 * ⚠️ 문구 박스는 피그마가 `h-154` 고정이지만 여기서는 `min-h`. 국문/독문 설명이 두 줄로 늘어나는데
 * 고정 높이면 아래 제품 아트와 겹친다. 데스크톱 1440 · 영문에서는 정확히 154px 로 떨어진다.
 *
 * ⚠️ Pro 카드 아트는 **베이크된 PNG 를 쓰지 않는다**. 시안 HTML 은 그라디언트가 구워진
 * 544x336 PNG 한 장을 쓰는데, 카드 폭이 708 이 아닌 구간(예: 1024 뷰포트)에서는 구워진
 * 그라디언트와 카드의 실제 CSS 그라디언트가 어긋나 이음매가 보인다.
 * 피그마 원본대로 **물결(원 4개) + 수트 PNG** 를 카드의 살아있는 그라디언트 위에 올린다.
 */

const BASE_PATH = "/images/home/main-renewal";

/** 피그마 Pro 카드 배경(28983:88606). 중간 정지점(#434C53 50%)을 빠뜨리면 명암이 달라진다. */
const PRO_BACKGROUND =
  "radial-gradient(71.07% 71.07% at 50% 100%, #5C676F 0%, #434C53 50%, #293137 100%)";

type Suit = {
  key: string;
  /** 영문 고정 라벨 — 번역하지 않는다 */
  eyebrow: string;
  /** 제품명 — 번역하지 않는다 */
  name: string;
  description: string;
  detailHref: string;
};

const AIR: Suit = {
  key: "air",
  eyebrow: "LIGHT & FREE",
  name: "TactSuit Air",
  description: "Light to wear, easy to move.",
  detailHref: "/shop/buy-tactsuit/tactsuit-air",
};

const PRO: Suit = {
  key: "pro",
  eyebrow: "ULTIMATE HAPTICS",
  name: "TactSuit Pro",
  description: "Power refined for ultimate immersion.",
  detailHref: "/shop/buy-tactsuit/tactsuit-pro",
};

export default function ChooseTactSuit({ className }: { className?: string }) {
  const { t } = useTrans(["index"]);
  const { ref, className: delayClassName, delayFunc } = useStaggerAnimation({});

  return (
    <section className={cn("w-full px-4 laptop:px-10", className)}>
      <div className="mx-auto flex w-full max-w-screen-max flex-col items-center">
        <SectionHeading
          title={t("Choose the Right TactSuit for You")}
          description={t(
            "Start light with Air, or choose Pro for stronger, more precise haptics.",
          )}
        />

        <div
          ref={ref}
          className="mt-6 grid w-full grid-cols-1 gap-4 laptop:mt-8 laptop:grid-cols-2 laptop:gap-6"
        >
          <ProductCardLink
            href={AIR.detailHref}
            label={AIR.name}
            style={{ transitionDelay: delayFunc(0) }}
            className={cn(
              "group flex flex-col items-center justify-start gap-5 overflow-hidden rounded-2xl bg-coolgray-10 px-5 py-6",
              "laptop:h-[560px] laptop:justify-between laptop:gap-0 laptop:py-[30px]",
              delayClassName,
            )}
          >
            <Contents suit={AIR} />
            {/* 피그마 300x300 박스 안에서 실제 이미지는 inset 8% (=276px) 를 차지한다. */}
            <div className="relative size-[220px] shrink-0 laptop:size-[300px]">
              {/**
               * ★**호버 배율을 이 이미지에 직접 주면 `scale-[0.84]` 를 덮어쓴다.**
               *
               * `.hover-scale` 은 `group-hover:scale-105` 라 같은 속성(transform)을 쓴다 —
               * 이미지에 함께 걸면 84% → **105%** 로 튄다(실측: `matrix(0.84…)` →
               * `matrix(1.05…)`). 5% 확대가 아니라 **25% 점프**다.
               *
               * 그래서 래퍼를 하나 두고 **변환을 중첩**시킨다. 두 transform 이 곱해져
               * 0.84 × 1.05 = 0.882 가 되고, 공용 클래스는 손대지 않는다.
               */}
              <div className="laptop:hover-scale absolute inset-0">
                {/* ★`alt=""` — 장식이다. 접근 이름은 카드 링크의 `aria-label` 이 갖는다
                    (`ProductCardLink` docblock 참조). 여기 제품명을 또 넣으면 스크린리더가
                    링크 안에서 같은 이름을 두 번 읽는다. */}
                <Image
                  src={`${BASE_PATH}/s4-air.png`}
                  alt=""
                  fill
                  sizes="(max-width: 1023px) 220px, 300px"
                  className="scale-[0.84] object-contain"
                />
              </div>
            </div>
          </ProductCardLink>

          <ProductCardLink
            href={PRO.detailHref}
            label={PRO.name}
            style={{
              transitionDelay: delayFunc(1),
              background: PRO_BACKGROUND,
            }}
            className={cn(
              // 모바일 시안은 Pro 카드 높이가 **아트에 고정**된다(464px). 문구가 한 줄 늘어도
              // 아트 위치(y=200)는 그대로라 `min-h` 로 둔다 — Air 카드만 문구 높이를 따라간다.
              "group relative flex min-h-[464px] flex-col items-center rounded-2xl px-5 py-6",
              "overflow-hidden laptop:h-[560px] laptop:min-h-0 laptop:px-0 laptop:pb-0 laptop:pt-[30px]",
              delayClassName,
            )}
          >
            <Contents suit={PRO} dark />
            <ProArtwork />
          </ProductCardLink>
        </div>
      </div>
    </section>
  );
}

/**
 * **카드 전체가 링크다** (#5674 · QA #5670 계열).
 *
 * QA 보고: *"제품 이미지를 눌러도 아무 일도 일어나지 않습니다."* 이 섹션에서 이동 가능한
 * 것은 `See Details` 하나였다.
 *
 * ## 왜 이미지만이 아니라 카드 전체인가
 *
 * 초판(#5674 첫 커밋)은 **이미지만** 감쌌다. 그때 실측한 클릭 가능 면적:
 *
 * ```
 *   카드 668×560   Air 이미지 링크 300×300 = 24%
 *                  Pro 이미지 링크 324×495 = 43%
 *   제목·설명·여백을 눌러도 이동하지 않았다(elementFromPoint 로 확인)
 * ```
 *
 * 즉 **같은 카드 열인데 눌리는 범위가 두 배 차이**였고, 사용자는 카드를 누를 것으로
 * 기대했다. 그래서 카드 루트를 링크로 올렸다 — 면적이 100% 가 된다.
 *
 * ## 링크 안 링크를 만들지 않는 방법 — CTA 를 **강등**했다
 *
 * `See Details` 와 이미지의 목적지가 **같은 상수**(`suit.detailHref`)임을 실측으로 확인했다
 * (2026-09-09 · 홈 2카드 · `/software/live` 4카드 전부 일치). 같은 곳으로 가는 링크가
 * 둘일 이유가 없으므로 CTA 를 `<span>` 으로 내렸다 — 보이는 것은 그대로 버튼이고,
 * 탭 스톱이 **카드당 1개**로 줄어든다(종전엔 같은 목적지를 두 번 지나갔다).
 *
 * ⚠️ `<Button asChild>` 를 유지한다 — `asChild` 없이 `<Button>` 을 쓰면 `<button>` 이
 * 렌더되고, 그건 **`<a>` 안에 올 수 없는 상호작용 요소**라 클릭이 깨진다.
 *
 * ## 접근 이름은 `aria-label` **하나**다
 *
 * `<a>` 에 `aria-label` 이 있으면 접근 이름 계산이 자식을 훑지 않으므로, 카드 안의 제목·
 * 설명·`alt` 가 이름에 섞이지 않는다(그러지 않으면 카드 텍스트 전체가 링크 이름이 된다).
 * 그래서 제품 이미지는 **`alt=""`(장식)** 으로 둔다 — 이름은 이미 링크가 갖고 있고,
 * 제품명은 카드 안 텍스트로도 보인다.
 *
 * ## 호버는 리포 공용 `.hover-scale`
 *
 * `globals.css` 의 그 클래스를 쓴다 — 리포가 **이미 널리 쓰는 표기**라 새로 만들면 같은
 * 동작이 두 벌이 된다. `group-hover:` 라 **조상에 `.group` 이 있어야** 돌므로 두 카드
 * 루트에 `group` 을 붙였다. `laptop:` 은 모바일에 호버가 없기 때문이다 — 모바일은 탭
 * 이동만 되면 된다.
 *
 * ⚠️ **사용 파일 수를 숫자로 적지 않는다.** 초판이 #5671 의 「17개 파일」을 옮겨 적었는데
 * 그 값은 base 기준이고 **이 PR 이 늘렸다**(실측 2026-09-09: base 17). 시점 없는 절대값은
 * 그 자리에서 낡는다 — 필요하면 세어라:
 *
 * ```bash
 * git grep -l hover-scale -- src | grep '\.tsx$' | wc -l
 * ```
 *
 * ⚠️ 위 명령에 `src` 하위 glob 을 쓰지 않는다 — `*` 와 `/` 가 붙으면
 * **JSDoc 주석을 조기 종료**시킨다(실측: `TS1109 Expression expected` 2건).
 *
 * ## 수용한 것 — 카드 안 텍스트 드래그 선택이 어려워진다
 *
 * 카드 전체가 링크이므로 본문을 드래그하면 링크 드래그로 잡힌다. 리포 선례
 * (~~`tactsuit/main/ProductCard.tsx`~~ — 렌더 경로가 없는 고아라 2026-10-01 삭제)도 카드 전체를
 * `<Link>` 로 감싸고 있어 표기를 맞췄다.
 * 대안(카드 크기 투명 오버레이 링크)은 CTA 를 링크로 남기는 대신 **같은 목적지 탭 스톱이
 * 2개**로 남고 리포 선례가 0건이라 택하지 않았다.
 */
function ProductCardLink({
  href,
  label,
  className,
  style,
  children,
}: React.PropsWithChildren<{
  href: string;
  label: string;
  className?: string;
  style?: React.CSSProperties;
}>) {
  return (
    <Link href={href} aria-label={label} className={className} style={style}>
      {children}
    </Link>
  );
}

function Contents({ suit, dark }: { suit: Suit; dark?: boolean }) {
  const { t } = useTrans(["index"]);

  return (
    <div
      className={cn(
        "flex w-full flex-col items-center justify-center gap-5",
        "laptop:min-h-[154px] laptop:gap-6",
        dark && "laptop:w-[632px] laptop:max-w-full",
      )}
    >
      <div className="flex w-full flex-col items-center gap-2 text-center laptop:gap-3">
        <p
          className={cn(
            "text-sm font-500 tracking-[0.5px]",
            dark ? "text-blue-30" : "text-red-60",
          )}
        >
          {suit.eyebrow}
        </p>
        <p
          className={cn(
            "text-2xl font-400",
            dark ? "text-white" : "text-coolgray-90",
          )}
        >
          {suit.name}
        </p>
        <p
          className={cn(
            "whitespace-pre-wrap text-base tracking-[0.25px]",
            dark ? "text-white" : "text-coolgray-90",
          )}
        >
          {t(suit.description)}
        </p>
      </div>

      {/* ★버튼 **하나** — 제품 상세로 가는 primary (#5289 · #5303).

          ⚠️ 번역 키는 **`See Details` 를 쓴다**. 시안의 영문 라벨은 "Learn More" 지만
          사전의 `Learn More` 키는 국문이 이미 '더 알아보기' 이고 다른 화면 8곳이 쓴다 —
          그 값을 시안 문구('자세히 보기')로 바꾸면 그쪽이 같이 바뀐다.
          `See Details` 는 이 카드 말고 쓰는 데가 없어서 **영문 값만 "Learn More" 로** 두고
          국문은 시안대로 '자세히 보기' 를 유지한다. (en/index.json 참조) */}
      <div className="flex flex-wrap justify-center gap-2">
        <Button
          asChild
          variant="blue-fill"
          shape="pill"
          size="sm"
          className="px-6"
        >
          <span>{t("See Details")}</span>
        </Button>
      </div>
    </div>
  );
}

/**
 * Pro 카드 아트 — 피그마 `img_tactsuit_pro_with_waves_event`(데스크톱 29042:152061 / 모바일 29042:152297).
 *
 * 두 보드 모두 **정사각 아트 박스를 카드 위쪽에 고정해 아래를 잘라내는** 구조다. 잘리는 비율이 다르다.
 *   - 데스크톱: 544 박스가 카드 y=238 → 560-238 = 322 노출(59.2%)
 *   - 모바일  : 330 박스가 카드 y=200 → 464-200 = 264 노출(80.0%)
 * 박스 중심이 카드 중심과 같아서(모바일 x=-1, 데스크톱 354=중앙) 가운데 정렬로 두면
 * 카드가 좁아져도 물결만 균등하게 잘리고 수트는 그대로 남는다.
 *
 * 안쪽은 항상 544 좌표계로 그리고 배율만 바꾼다 — 330/544 = 0.6066.
 */
function ProArtwork() {
  return (
    /**
     * ★**아트는 전부 장식이다** — `aria-hidden` + `pointer-events-none` 을 래퍼에 둔다.
     *
     * #5674 초판은 이미지만 링크로 만들었기 때문에 여기서 `aria-hidden` 을 `<svg>` 로
     * 옮기고 이미지에 `pointer-events-auto` 를 되살려야 했다. **카드 전체가 링크가 되면서
     * 그 우회가 필요 없어졌다** — 이동은 카드가 담당하므로 아트는 순수 장식으로 되돌린다.
     *
     * `pointer-events-none` 이라 수트를 눌러도 이 요소가 히트 타깃이 되지 않고, 히트
     * 테스트가 **조상(카드 링크)** 으로 올라간다 → 수트를 눌러도 제품 상세로 간다.
     * 실측으로 확인했다(아래 PR 본문의 `elementFromPoint` 표).
     *
     * ⚠️ 아트 박스는 카드보다 크다(laptop 544 박스가 카드 y=238 에서 시작 → 782 > 560).
     * 카드가 `overflow-hidden` 이라 시각·히트테스트가 함께 잘린다 — **카드 밖을 눌러도
     * 이동하지 않는지**를 실측으로 잠갔다.
     */
    <div
      aria-hidden
      className={cn(
        "pointer-events-none absolute left-1/2 top-[200px] h-[264px] w-[330px] -translate-x-1/2 overflow-hidden",
        "laptop:top-[238px] laptop:h-[544px] laptop:w-[544px] laptop:overflow-visible",
      )}
    >
      <div className="absolute left-1/2 top-0 size-[544px] origin-top -translate-x-1/2 scale-[0.6066] laptop:scale-100">
        <svg
          viewBox="0 0 514 514"
          fill="none"
          className="absolute left-[15px] top-[30px] size-[514px]"
        >
          <circle
            cx="257"
            cy="257"
            r="256"
            stroke="white"
            strokeWidth="2"
            opacity="0.1"
          />
          <circle
            cx="257"
            cy="257"
            r="204.6"
            stroke="white"
            strokeWidth="2"
            opacity="0.1"
          />
          <circle
            cx="257"
            cy="257"
            r="153.2"
            stroke="white"
            strokeWidth="2"
            opacity="0.1"
          />
          <circle
            cx="257"
            cy="257"
            r="101.8"
            stroke="white"
            strokeWidth="2"
            opacity="0.1"
          />
        </svg>
        <div className="absolute left-1/2 top-0 h-[495px] w-[324px] -translate-x-1/2">
          <Image
            src={`${BASE_PATH}/s4-pro-suit.png`}
            alt=""
            width={324}
            height={495}
            sizes="324px"
            className="laptop:hover-scale size-full object-contain"
          />
        </div>
      </div>
    </div>
  );
}
